// /memberships/new — Create Membership Plan (PAGES.md §3.94)
//
// Three-column lg layout: form (2/3) + sticky live preview (1/3). The
// preview uses the shared MembershipPreviewCard so what you see here is
// byte-for-byte what members will see in their wallet.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer, ErrorBoundary, Button, Alert, useToast } from '@bbpos/ui';
import { ApiError } from '@/lib/api-client';
import {
  useCreateMembershipPlan,
  MembershipPreviewCard,
} from '@/features/memberships';
import type { MembershipPlan } from '@/features/memberships';
import {
  MembershipPlanForm,
  validateMembershipForm,
  INITIAL_FORM_STATE,
  type MembershipFormErrors,
  type MembershipFormState,
} from './MembershipPlanForm';
import { getSubunitMultiplier } from '@bbpos/utils';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';

function MembershipPlanCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [form, setForm] = useState<MembershipFormState>(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState<MembershipFormErrors>({});
  const create = useCreateMembershipPlan();

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    const result = validateMembershipForm(form, currency);
    setErrors(result.errors);
    if (!result.ok || !result.payload) return;
    try {
      const created = await create.mutateAsync(result.payload);
      toast({
        variant: 'success',
        title: 'Plan created',
        description: `${created.name} is ready to enroll members.`,
      });
      navigate(`/memberships/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        const msg = err.message || '';
        if (/name|unique|duplicate|already exists/i.test(msg)) {
          setErrors((prev) => ({
            ...prev,
            name: 'A plan with this name already exists. Pick a different name.',
          }));
          return;
        }
        toast({ variant: 'error', title: 'Could not create plan', description: msg });
        return;
      }
      toast({
        variant: 'error',
        title: 'Could not create plan',
        description: 'Please try again.',
      });
    }
  }

  // Keyboard shortcuts: Ctrl/Cmd+Enter submit, Esc cancel.
  // Ref pattern so we don't rebind the listener on every keystroke.
  const submitRef = useRef<() => Promise<void>>(() => Promise.resolve());
  submitRef.current = () => handleSubmit();
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        void submitRef.current();
      } else if (e.key === 'Escape') {
        navigate('/memberships');
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Build a MembershipPlan-shaped preview object from the form state.
  // Memoised so the preview card only re-renders when something it renders changes.
  const previewPlan = useMemo<Partial<MembershipPlan> & { tier_level: MembershipPlan['tier_level'] }>(
    () => ({
      name: form.name,
      tier_level: form.tier_level,
      description: form.description,
      fee: Math.max(0, Math.round(Number(form.fee_rupees) * getSubunitMultiplier(currency)) || 0),
      duration_days: Math.max(0, Math.floor(Number(form.duration_days) || 0)),
      discount_percentage: Math.max(0, Number(form.discount_percentage) || 0),
      points_multiplier: Math.max(1, Number(form.points_multiplier) || 1),
      priority_service: form.priority_service,
      auto_renewal: form.auto_renewal,
      is_active: form.is_active,
    }),
    [form],
  );

  return (
    <div className="animate-fade-in">
      {/* Hero — success/teal gradient to signal creation energy */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-success-600 via-teal-500 to-cyan-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(16,185,129,0.55)]">
        <div className="pointer-events-none absolute -top-24 -right-10 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            New Plan
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Create Membership Plan</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Define pricing, duration, and benefits. The preview on the right updates as you type.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Form — 2 of 3 columns on lg */}
        <form
          onSubmit={handleSubmit}
          className="lg:col-span-2 rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-6 sm:p-7 animate-slide-up"
        >
          {create.isError && !errors.name ? (
            <div className="mb-5">
              <Alert
                variant="danger"
                title="Could not save"
                description="Check the fields below and try again."
              />
            </div>
          ) : null}

          <MembershipPlanForm
            value={form}
            onChange={setForm}
            errors={errors}
            footer={
              <div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button variant="ghost" type="button" onClick={() => navigate('/memberships')}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" loading={create.isPending}>
                    Create Plan
                  </Button>
                </div>
                <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500 text-right">
                  Press{' '}
                  <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">
                    Ctrl+Enter
                  </kbd>{' '}
                  to save ·{' '}
                  <kbd className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 font-mono text-[10px]">
                    Esc
                  </kbd>{' '}
                  to cancel
                </p>
              </div>
            }
          />
        </form>

        {/* Live preview — 1 of 3 columns on lg, sticky */}
        <aside className="lg:col-span-1 animate-slide-up" style={{ animationDelay: '60ms' }}>
          <div className="sticky top-4">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Live Preview — how members will see this plan
            </p>
            <MembershipPreviewCard plan={previewPlan} variant="preview" />
          </div>
        </aside>
      </div>
    </div>
  );
}

export function MembershipPlanCreatePage() {
  return (
    <PageContainer
      title="New Membership Plan"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Memberships', href: '/memberships' },
        { label: 'New Plan' },
      ]}
    >
      <ErrorBoundary resetKey="memberships-new">
        <MembershipPlanCreateContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
